<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ImageIcon } from 'lucide-vue-next';
import { activeDashboardUserId,authRequired, getApiToken } from '../composables/api';
import { apiUrl } from '../composables/api-url';
import { cachedAsset,abortableRead } from '../composables/read-work';
import { useVisiblePreview } from '../composables/visible-preview';

const props = defineProps<{ src: string; alt?: string }>();
const objectUrl = ref('');
const failed = ref(false);
let controller: AbortController | undefined;
const {element,visible}=useVisiblePreview();

watch(() => [props.src,visible.value,activeDashboardUserId.value], load, { immediate: true });
onMounted(()=>window.addEventListener('eqover:assets-changed',load));
onBeforeUnmount(()=>{window.removeEventListener('eqover:assets-changed',load);clear();});

async function load() {
  clear();
  failed.value = false;
  if (!props.src||!visible.value) return;
  const currentController=new AbortController();controller=currentController;
  const token = getApiToken();
  const source=props.src;
  try {
    const blob=await abortableRead(cachedAsset(JSON.stringify([token,activeDashboardUserId.value,source,'image']),async(signal)=>{
      const response=await fetch(apiUrl(source),{signal,headers:{Accept:'image/*',...(token?{Authorization:`Bearer ${token}`}:{})}});
      if(response.status===401&&getApiToken()===token)authRequired.value=true;
      if(!response.ok)throw new Error(`HTTP ${response.status}`);
      return response.blob();
    }),currentController.signal);
    if(controller!==currentController)return;
    if (!blob.type.startsWith('image/')) throw new Error('El archivo no es una imagen visible.');
    objectUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    if (controller===currentController && (error as Error).name !== 'AbortError') failed.value = true;
  }
}

function clear() {
  controller?.abort();
  controller = undefined;
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = '';
}
</script>

<template>
  <span ref="element" class="api-image" :class="{ failed }">
    <img v-if="objectUrl" :src="objectUrl" :alt="alt || ''" />
    <slot v-else name="fallback"><ImageIcon :size="18" aria-hidden="true" /></slot>
  </span>
</template>

<style scoped>
.api-image{display:grid;place-items:center;overflow:hidden;color:#a0a7b5;background:#eef0f3}.api-image img{width:100%;height:100%;display:block;object-fit:cover}.api-image.failed{color:#c1c6cf;background:#f4f5f6}
</style>
